import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Plus, Filter, ArrowUpCircle, Check, Ban, Repeat, Paperclip, Users, Bot, ShieldCheck, Receipt, ChevronLeft, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { FormField } from '@/components/common/FormSection';
import { AmountText, FinanceStatusBadge, TransactionTypeIcon, toneForTransactionType } from '@/components/finance-v2/FinanceBadges';
import type { Transaction, Account, TransactionType } from '../types/financeV2.types';
import { financeV2Service, type CreateTransactionDto, type CreateRecurringTransactionDto, type TransactionFilters, type TransactionStatusFilter } from '../services/financeV2Service';
import { TransactionFiltersPanel } from '../components/finance-v2/TransactionFiltersPanel';
import { STATUS_FILTERS, situationLabel } from '@/lib/finance';
import { useAuth } from '@/contexts/AuthContext';
import { isSocio } from '@/lib/roles';
import { TransactionAttachments } from '../components/TransactionAttachments';
import { parseDbTimestamp } from '../utils/datetime';
import { clientService } from '../services/client-service';
import type { Client } from '../types/client';
import { getErrorMessage } from '../utils/error.utils';

const STATUS_VALUES = STATUS_FILTERS.map((s) => s.value) as readonly string[];

/** Filtros iniciais vindos da URL (ex.: aviso de vencimento no sino, ficha do cliente/caso). */
export function filtersFromSearch(params: URLSearchParams): TransactionFilters {
  const f: TransactionFilters = {};
  const status = params.get('status');
  if (status && STATUS_VALUES.includes(status)) f.status = status as TransactionStatusFilter;
  for (const key of ['clientId', 'caseId', 'dueDateFrom', 'dueDateTo'] as const) {
    const v = params.get(key);
    if (v) f[key] = v;
  }
  if (params.get('needsConfirmation') === 'true') f.needsConfirmation = true;
  return f;
}

export function FinanceV2TransactionsPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const canConfirm = isSocio(user?.role);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filters, setFilters] = useState<TransactionFilters>(() => filtersFromSearch(searchParams));
  const [showFilters, setShowFilters] = useState(() => Object.keys(filtersFromSearch(searchParams)).length > 0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [attachmentsTransactionId, setAttachmentsTransactionId] = useState<string | null>(null);

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await financeV2Service.transactions.list({ ...filters, page, limit: 20 });
      setTransactions(response.data.transactions);
      setTotalPages(response.data.pagination.totalPages);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar transações'));
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const loadAccounts = async () => {
    try {
      const response = await financeV2Service.accounts.list({ isActive: true });
      setAccounts(response.data.accounts);
    } catch (err: unknown) {
      console.error('Erro ao carregar contas:', err);
    }
  };

  const handlePayTransaction = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowPayModal(true);
  };

  const handleConfirmTransaction = async (transaction: Transaction) => {
    try {
      await financeV2Service.transactions.confirm(transaction.id);
      loadTransactions();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao confirmar lançamento'));
    }
  };

  const handleCancelTransaction = async (transaction: Transaction) => {
    if (!window.confirm('Tem certeza que deseja cancelar esta transação?')) {
      return;
    }

    try {
      await financeV2Service.transactions.cancel(transaction.id, { reason: 'Cancelado pelo usuário' });
      loadTransactions();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao cancelar transação'));
    }
  };

  const handleOpenAttachments = (transactionId: string) => {
    setAttachmentsTransactionId(transactionId);
    setShowAttachments(true);
  };

  const handleCloseAttachments = () => {
    setShowAttachments(false);
    setAttachmentsTransactionId(null);
  };

  const handleSearch = (searchTerm: string) => {
    setFilters({ ...filters, search: searchTerm });
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setPage(1);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return parseDbTimestamp(dateString)?.toLocaleDateString('pt-BR') ?? '-';
  };

  const getTypeLabel = (type: TransactionType) => {
    return type === 'INCOME' ? 'Receita' : 'Despesa';
  };

  /** Situação pela regra única de vencido (campo isOverdue calculado no servidor). */
  const getStatusBadge = (transaction: Transaction) => (
    <div className="flex flex-col items-start gap-1">
      <FinanceStatusBadge status={situationLabel(transaction)} />
      {transaction.createdByKind === 'AGENT' && (
        <Badge variant="outline">
          <Bot className="size-3" aria-hidden="true" />
          {transaction.needsConfirmation ? 'Hermes · aguarda sócio' : 'Hermes'}
        </Badge>
      )}
    </div>
  );

  const getAccountName = (accountId: string) => {
    const account = accounts.find(a => a.id === accountId);
    return account?.name || 'Conta não encontrada';
  };

  const isOutstanding = (transaction: Transaction) => transaction.status === 'PENDING' || transaction.status === 'OVERDUE';

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <Receipt className="size-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Transações</h1>
          </div>
          <p className="text-sm text-muted-foreground">Gerencie suas receitas e despesas</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="size-4" /> Nova Transação
        </Button>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            type="text"
            aria-label="Buscar transações"
            placeholder="Buscar transações..."
            className="pl-8"
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        <Button variant="outline" onClick={() => setShowFilters(!showFilters)} aria-expanded={showFilters}>
          <Filter className="size-4" /> Filtros
        </Button>
      </div>

      {showFilters && (
        <TransactionFiltersPanel
          filters={filters}
          accounts={accounts}
          onChange={(next) => { setFilters(next); setPage(1); }}
          onClear={clearFilters}
        />
      )}

      {notice && (
        <div role="status" className="rounded-lg border bg-muted/40 p-3 text-sm">
          {notice}
        </div>
      )}

      {error && (
        <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          <span className="sr-only">Carregando transações...</span>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14" />)}
        </div>
      ) : transactions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <ArrowUpCircle className="size-10 text-muted-foreground" aria-hidden="true" />
            <h3 className="text-sm font-medium">Nenhuma transação encontrada</h3>
            <p className="text-sm text-muted-foreground">Comece criando uma nova transação.</p>
            <Button className="mt-2" onClick={() => setShowCreateModal(true)}>
              <Plus className="size-4" /> Nova Transação
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="min-w-0 py-0">
          <div className="min-w-0 overflow-x-auto">
            <Table className="min-w-[880px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Conta</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Vencimento</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell className="whitespace-normal">
                      <div className="flex min-w-0 items-center gap-3">
                        <TransactionTypeIcon type={transaction.transactionType} />
                        <div className="min-w-0">
                          <div className="break-words text-sm font-medium">{transaction.description}</div>
                          {transaction.isRecurring && transaction.installmentNumber && (
                            <div className="text-xs text-muted-foreground">
                              Parcela {transaction.installmentNumber}/{transaction.totalInstallments}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getTypeLabel(transaction.transactionType)}</TableCell>
                    <TableCell>{getAccountName(transaction.accountId)}</TableCell>
                    <TableCell>
                      <AmountText tone={toneForTransactionType(transaction.transactionType)}>
                        {formatCurrency(transaction.amount)}
                      </AmountText>
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">
                      {formatDate(transaction.dueDate)}
                      {transaction.paidDate && (
                        <div className="text-xs text-primary">
                          Pago em {formatDate(transaction.paidDate)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(transaction)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon-sm" onClick={() => handleOpenAttachments(transaction.id)} aria-label="Anexos">
                              <Paperclip className="size-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Anexos</TooltipContent>
                        </Tooltip>
                        {transaction.needsConfirmation && canConfirm && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => handleConfirmTransaction(transaction)}
                                aria-label="Confirmar lançamento do Hermes"
                                className="text-primary hover:text-primary"
                              >
                                <ShieldCheck className="size-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Confirmar (sócio)</TooltipContent>
                          </Tooltip>
                        )}
                        {isOutstanding(transaction) && (!transaction.needsConfirmation || canConfirm) && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => handlePayTransaction(transaction)}
                                aria-label="Pagar"
                                className="text-primary hover:text-primary"
                              >
                                <Check className="size-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Pagar</TooltipContent>
                          </Tooltip>
                        )}
                        {isOutstanding(transaction) && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                onClick={() => handleCancelTransaction(transaction)}
                                aria-label="Cancelar"
                                className="text-destructive hover:text-destructive"
                              >
                                <Ban className="size-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Cancelar</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3">
              <p className="text-sm text-muted-foreground">Página {page} de {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 1}>
                  <ChevronLeft className="size-4" /> Anterior
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                  Próxima <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {showCreateModal && (
        <CreateTransactionModal
          accounts={accounts}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadTransactions();
          }}
        />
      )}

      {showPayModal && selectedTransaction && (
        <PayTransactionModal
          transaction={selectedTransaction}
          onClose={() => {
            setShowPayModal(false);
            setSelectedTransaction(null);
          }}
          onSuccess={(message) => {
            setShowPayModal(false);
            setSelectedTransaction(null);
            setNotice(message ?? null);
            loadTransactions();
          }}
        />
      )}

      {showAttachments && attachmentsTransactionId && (
        <TransactionAttachments
          transactionId={attachmentsTransactionId}
          onClose={handleCloseAttachments}
        />
      )}
    </div>
  );
}

// Create Transaction Modal Component
function CreateTransactionModal({
  accounts,
  onClose,
  onSuccess
}: {
  accounts: Account[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  // TODOS os hooks devem estar no topo, antes de qualquer early return
  const [clients, setClients] = useState<Client[]>([]);
  const [, setLoadingClients] = useState(true);
  const [isRecurring, setIsRecurring] = useState(false);
  const [formData, setFormData] = useState<CreateTransactionDto>({
    description: '',
    amount: 0,
    type: 'EXPENSE',
    accountId: accounts.length > 0 ? accounts[0].id : '',
    dueDate: new Date().toISOString().split('T')[0]
  });
  const [recurringData, setRecurringData] = useState<CreateRecurringTransactionDto>({
    description: '',
    amount: 0,
    type: 'EXPENSE',
    accountId: accounts.length > 0 ? accounts[0].id : '',
    startDate: new Date().toISOString().split('T')[0],
    installments: 12,
    intervalType: 'MONTHLY',
    intervalValue: 1
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Carregar clientes quando o modal abre
  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      const response = await clientService.getAllClients(
        { status: 'ACTIVE' },
        { page: 1, limit: 1000, sort: 'createdAt:desc' }
      );
      setClients(response.clients || []);
    } catch (err) {
      console.error('❌ Erro ao carregar clientes:', err);
    } finally {
      setLoadingClients(false);
    }
  };

  // Helper function to get client display name
  const getClientDisplayName = (client: Client): string => {
    if (client.clientType === 'PERSON') {
      return client.personalData?.fullName ||
             `${client.personalData?.firstName || ''} ${client.personalData?.lastName || ''}`.trim() ||
             'Cliente sem nome';
    } else {
      return client.companyData?.companyName ||
             client.companyData?.tradingName ||
             'Empresa sem nome';
    }
  };

  if (accounts.length === 0) {
    return (
      <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Nenhuma conta disponível</DialogTitle>
            <DialogDescription>Cadastre pelo menos uma conta antes de criar transações.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      if (isRecurring) {
        await financeV2Service.transactions.createRecurring(recurringData);
      } else {
        await financeV2Service.transactions.create(formData);
      }
      onSuccess();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao criar transação'));
    } finally {
      setLoading(false);
    }
  };

  const intervalLabel = { DAILY: 'diária', WEEKLY: 'semanal', MONTHLY: 'mensal', YEARLY: 'anual' }[recurringData.intervalType || 'MONTHLY'];

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="lg" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Nova Transação</DialogTitle>
          <DialogDescription>Registre uma receita ou despesa.</DialogDescription>
        </DialogHeader>

        {error && (
          <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
            {error}
          </div>
        )}

        <form id="create-transaction-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex items-center gap-2 rounded-lg border bg-muted/40 p-3">
            <input
              type="checkbox"
              id="isRecurring"
              checked={isRecurring}
              onChange={(e) => setIsRecurring(e.target.checked)}
              className="size-4 rounded-sm border-input accent-primary"
            />
            <Label htmlFor="isRecurring" className="cursor-pointer">
              <Repeat className="size-4 text-muted-foreground" aria-hidden="true" />
              Transação Recorrente (gerar múltiplas transações)
            </Label>
          </div>

          <FormField label="Descrição *" htmlFor="tx-description">
            <Input
              id="tx-description"
              type="text"
              required
              value={isRecurring ? recurringData.description : formData.description}
              onChange={(e) => {
                if (isRecurring) {
                  setRecurringData({ ...recurringData, description: e.target.value });
                } else {
                  setFormData({ ...formData, description: e.target.value });
                }
              }}
            />
          </FormField>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Tipo *" htmlFor="tx-type">
              <NativeSelect
                id="tx-type"
                required
                value={isRecurring ? recurringData.type : formData.type}
                onChange={(e) => {
                  const type = e.target.value as TransactionType;
                  if (isRecurring) {
                    setRecurringData({ ...recurringData, type });
                  } else {
                    setFormData({ ...formData, type });
                  }
                }}
              >
                <option value="INCOME">Receita</option>
                <option value="EXPENSE">Despesa</option>
              </NativeSelect>
            </FormField>

            <FormField label="Valor *" htmlFor="tx-amount">
              <Input
                id="tx-amount"
                type="number"
                step="0.01"
                required
                value={(isRecurring ? recurringData.amount : formData.amount) || ''}
                onChange={(e) => {
                  const amount = parseFloat(e.target.value) || 0;
                  if (isRecurring) {
                    setRecurringData({ ...recurringData, amount });
                  } else {
                    setFormData({ ...formData, amount });
                  }
                }}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Conta *" htmlFor="tx-account">
              <NativeSelect
                id="tx-account"
                required
                value={isRecurring ? recurringData.accountId : formData.accountId}
                onChange={(e) => {
                  if (isRecurring) {
                    setRecurringData({ ...recurringData, accountId: e.target.value });
                  } else {
                    setFormData({ ...formData, accountId: e.target.value });
                  }
                }}
              >
                {accounts.map(account => (
                  <option key={account.id} value={account.id}>{account.name}</option>
                ))}
              </NativeSelect>
            </FormField>

            <FormField label={isRecurring ? 'Primeiro Vencimento *' : 'Vencimento *'} htmlFor="tx-due-date">
              <Input
                id="tx-due-date"
                type="date"
                required
                value={isRecurring ? recurringData.startDate : formData.dueDate}
                onChange={(e) => {
                  if (isRecurring) {
                    setRecurringData({ ...recurringData, startDate: e.target.value });
                  } else {
                    setFormData({ ...formData, dueDate: e.target.value });
                  }
                }}
              />
            </FormField>
          </div>

          <FormField
            htmlFor="tx-client"
            label={
              <>
                <Users className="size-4 text-muted-foreground" aria-hidden="true" />
                Cliente (Opcional) {clients.length > 0 && <span className="text-xs font-normal text-muted-foreground">({clients.length} disponíveis)</span>}
              </>
            }
            hint={clients.length === 0
              ? 'Nenhum cliente ativo encontrado'
              : 'Vincule esta transação a um cliente para facilitar o acompanhamento'}
          >
            <NativeSelect
              id="tx-client"
              value={isRecurring ? (recurringData.clientId || '') : (formData.clientId || '')}
              onChange={(e) => {
                const clientId = e.target.value || undefined;
                if (isRecurring) {
                  setRecurringData({ ...recurringData, clientId });
                } else {
                  setFormData({ ...formData, clientId });
                }
              }}
            >
              <option value="">Nenhum cliente vinculado</option>
              {clients.length === 0 ? (
                <option disabled>Carregando clientes...</option>
              ) : (
                clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {getClientDisplayName(client)}
                  </option>
                ))
              )}
            </NativeSelect>
          </FormField>

          {isRecurring && (
            <div className="flex flex-col gap-4 rounded-lg border bg-muted/40 p-3">
              <h3 className="flex items-center gap-2 text-sm font-medium">
                <Repeat className="size-4 text-muted-foreground" aria-hidden="true" />
                Configurações de Recorrência
              </h3>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField label="Frequência *" htmlFor="tx-interval">
                  <NativeSelect
                    id="tx-interval"
                    required
                    value={recurringData.intervalType || 'MONTHLY'}
                    onChange={(e) => setRecurringData({ ...recurringData, intervalType: e.target.value as 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY' })}
                  >
                    <option value="DAILY">Diária</option>
                    <option value="WEEKLY">Semanal</option>
                    <option value="MONTHLY">Mensal</option>
                    <option value="YEARLY">Anual</option>
                  </NativeSelect>
                </FormField>

                <FormField label="Quantidade de Parcelas *" htmlFor="tx-installments">
                  <Input
                    id="tx-installments"
                    type="number"
                    min="2"
                    max="360"
                    required
                    value={recurringData.installments}
                    onChange={(e) => setRecurringData({ ...recurringData, installments: parseInt(e.target.value) || 2 })}
                  />
                </FormField>
              </div>

              <p className="text-xs text-muted-foreground">
                Serão criadas <strong>{recurringData.installments} transações</strong> com frequência {intervalLabel}
              </p>
            </div>
          )}

          <FormField label="Observações" htmlFor="tx-notes">
            <Textarea
              id="tx-notes"
              rows={3}
              value={(isRecurring ? recurringData.notes : formData.notes) || ''}
              onChange={(e) => {
                if (isRecurring) {
                  setRecurringData({ ...recurringData, notes: e.target.value });
                } else {
                  setFormData({ ...formData, notes: e.target.value });
                }
              }}
            />
          </FormField>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" form="create-transaction-form" disabled={loading}>
            {loading ? 'Criando...' : isRecurring ? `Criar ${recurringData.installments} Transações` : 'Criar Transação'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Pay Transaction Modal Component
function PayTransactionModal({
  transaction,
  onClose,
  onSuccess
}: {
  transaction: Transaction;
  onClose: () => void;
  onSuccess: (message?: string) => void;
}) {
  const [paidDate, setPaidDate] = useState(new Date().toLocaleDateString('en-CA'));
  const [paidAmount, setPaidAmount] = useState(transaction.amount);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      const response = await financeV2Service.transactions.pay(transaction.id, { paidDate, paidAmount });
      onSuccess(response.data?.remainder ? response.message : undefined);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao pagar transação'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="sm" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Pagar Transação</DialogTitle>
          <DialogDescription>Informe a data e o valor efetivamente pago.</DialogDescription>
        </DialogHeader>

        {error && (
          <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
            {error}
          </div>
        )}

        <div className="rounded-lg border bg-muted/40 p-3">
          <div className="text-xs text-muted-foreground">Transação</div>
          <div className="break-words font-medium">{transaction.description}</div>
          <div className="mt-1 text-sm text-muted-foreground">
            Valor original: <span className="font-medium tabular-nums text-foreground">{formatCurrency(transaction.amount)}</span>
          </div>
        </div>

        <form id="pay-transaction-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          <FormField label="Data de Pagamento *" htmlFor="pay-date">
            <Input
              id="pay-date"
              type="date"
              required
              value={paidDate}
              onChange={(e) => setPaidDate(e.target.value)}
            />
          </FormField>

          <FormField
            label="Valor Pago *"
            htmlFor="pay-amount"
            hint={paidAmount > 0 && paidAmount < transaction.amount
              ? `Pagamento parcial: o saldo de ${formatCurrency(Math.round((transaction.amount - paidAmount) * 100) / 100)} continua em aberto num novo lançamento, com o mesmo vencimento.`
              : undefined}
          >
            <Input
              id="pay-amount"
              type="number"
              step="0.01"
              required
              value={paidAmount || ''}
              onChange={(e) => setPaidAmount(parseFloat(e.target.value) || 0)}
            />
          </FormField>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" form="pay-transaction-form" disabled={loading}>
            {loading ? 'Pagando...' : 'Confirmar Pagamento'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
